/**
 * Shared utilities for Zustand store slices.
 */

import type { FlashkaState } from '../../internal/store/flashka-store';
import type { SyncStorage } from '../../provider/sync-storage';
import { getSyncStorage } from '../../provider/sync-storage';

// Re-export for slices that need storage types
export type { SyncStorage };
export { getSyncStorage };

export type StateUpdater =
  | Partial<FlashkaState>
  | ((s: FlashkaState) => Partial<FlashkaState>);
export type SetFn = (update: StateUpdater) => void;

type SentryLike = {
  captureException?: (err: unknown, ctx?: { tags?: Record<string, string> }) => void;
};

export function reportSyncStorageError(err: unknown, label: string): void {
  console.error(`[SyncStorage] ${label} failed`, err);
  const sentry = (globalThis as { Sentry?: SentryLike }).Sentry;
  try {
    sentry?.captureException?.(err, { tags: { source: 'sync-storage', label } });
  } catch {
    // If Sentry capture itself throws, swallow — we already logged to console.
  }
}

/**
 * Update Zustand state and fire-and-forget persist to local storage.
 * Writes are synchronous now (Phase 3 ring buffer), so errors surface
 * via the storage adapter's own error pipeline — we still wrap the
 * `persist` callback in try/catch for any synchronous mistakes.
 */
export function mutate(
  set: SetFn,
  update: StateUpdater,
  persist: (storage: SyncStorage) => void,
) {
  set(update);
  try {
    persist(getSyncStorage());
  } catch (err) {
    reportSyncStorageError(err, 'mutate-sync');
  }
}

/**
 * Fire a single storage write with the same error handling as `mutate`.
 * Use at inline `for (...) storage.put(...)` sites that bypass `mutate`.
 */
export function safePut<T>(storage: SyncStorage, table: string, item: T): void {
  try {
    storage.put(table, item);
  } catch (err) {
    reportSyncStorageError(err, `put(${table})-sync`);
  }
}

/**
 * Write a batch through `storage.bulkPut` (web Dexie: one IndexedDB
 * transaction instead of one per row — per-row puts detonate IDB on large
 * generation batches; mobile Kv/Hybrid: one buffered `putMany` op). Falls
 * back to per-item `safePut` for a backend without `bulkPut`.
 */
export function safeBulkPut<T>(storage: SyncStorage, table: string, items: T[]): void {
  if (!storage.bulkPut) {
    for (const item of items) safePut(storage, table, item);
    return;
  }
  if (items.length === 0) return;
  try {
    storage.bulkPut(table, items).catch((err) => {
      reportSyncStorageError(err, `bulkPut(${table})-async`);
    });
  } catch (err) {
    reportSyncStorageError(err, `bulkPut(${table})-sync`);
  }
}

/**
 * Delete a batch of rows through `storage.bulkRemove` when the backend
 * provides it (one transaction), falling back to one `removeByKey` per key.
 * Same error handling as `safePut`.
 */
export function safeBulkRemove(
  storage: SyncStorage,
  table: string,
  keys: Array<string | unknown[]>,
): void {
  if (keys.length === 0) return;
  if (!storage.bulkRemove) {
    for (const key of keys) {
      try {
        storage.removeByKey(table, key);
      } catch (err) {
        reportSyncStorageError(err, `removeByKey(${table})-sync`);
      }
    }
    return;
  }
  try {
    storage.bulkRemove(table, keys);
  } catch (err) {
    reportSyncStorageError(err, `bulkRemove(${table})-sync`);
  }
}
